<template>
  <div class="container">
    <Header />
    <div class="content">
      <Toast class="toast" ref="toastInst" />
      <div class="title-wrapper">
        <div class="request-title">{{ requestDisplayTitle }}</div>
        <div class="request-desc">{{ requestDisplayDescription }}</div>
        <div class="upload-date" v-show="hasExpiration">{{ uploadDate }}</div>
      </div>
      <div class="body-wrapper">
        <div class="upload-status" v-show="isUploading || isUploaded">
          <ProgressBar v-show="isUploading" :icon="currentUploadType" :title="progressTitle" :text="progressText" :percentage="progressPercentage" />
          <div v-show="isUploadedSuccess" class="uploaded-complete-info">
            <img src="@/assets/icon_success.svg" class="uploaded-complete-icon" alt="Upload Success Icon" />
            <div class="uploaded-complete-text"> {{ uploadSuccessText }}</div>
          </div>
          <div v-show="isUploadedFailed" class="uploaded-complete-info">
            <img src="@/assets/icon_error.svg" class="uploaded-complete-icon" alt="Upload Failed Icon" />
            <div class="uploaded-complete-text"> {{ uploadFailedText }}</div>
          </div>
        </div>
        <div class="files-area" :style="{ height: filesAreaHeight, border: `1px ${uploadTaskItemFiles.length === 0 ? 'dashed' : 'solid'} var(--v-color-border-tier1)` }" @dragenter="handleDragEnter($event, uploadStatus)" @dragleave="handleDragLeave($event, uploadStatus)" @dragover.prevent @drop="handleDrop($event, uploadStatus)">
          <div class="drag-drop-area" :style="{ height: filesAreaHeight }" v-show="isDragging && (isIdle || isSelectedFiles)">
            <div class="drag-drop-icon-wraper">
              <div class="drag-drop-top-hint" />
              <div class="drag-drop-bottom-hint">{{ $T('upload', 'drop_here') }}</div>
            </div>
          </div>
          <UploadTask :uploadTaskItemFiles="uploadTaskItemFiles" :errMsg="uploadErrMsg" status @removeItems="removeItem" @previewItems="previewItem"/>
          <input type="file" ref="fileInput" style="display: none" @change="handleFileChange" multiple>
          <input type="file" ref="folderInput" style="display: none" @change="handleFolderChange" webkitdirectory>
          <div class="img-btn-wrapper" v-show="isIdle && uploadTaskItemFiles.length === 0 && isDragging === false">
            <img class="empty-file" draggable="false" alt="Upload Icon" />
            <div class="btn-desc-wrapper">
              <MenuButton v-if="isComputerDevice"
              :config="{
                buttonText: uploadAddItemText,
                defaultEnabled: true,
                height: 30,
              }" v-show="uploadTaskItemFiles.length === 0" @filePicker="openFilePicker" @folderPicker="openFolderPicker"/>
              <Button v-else
              :config="{
                buttonText: uploadAddItemText,
                defaultEnabled: true,
                height: 30,
                color: 'green',
              }" @click="openFilePicker" />
              <div class="upload-hint" v-show="uploadTaskItemFiles.length === 0">{{ uploadHintText }}</div>
            </div>
          </div>
        </div>
        <div class="bottom-area">
          <div class="username-msg-wrapper" v-show="isSelectedFiles">
            <div class="username-wrapper" >
              <label for="username" class="username-label">{{ $T('file_request', 'username_label') }}</label>
              <TextField v-model="userName" ref="nameField" @input="onUserNameInput" @compositionstart="onCompositionStart" @compositionend="onCompositionEnd"/>
            </div>
            <div class="error-message-container">
              <div class="error-space"></div>
              <div class="error-message">
                {{ userNameErrorMessage }}
              </div>
            </div>
          </div>
          <div class="foot-button-wrapper" v-show="isSelectedFiles">
            <MenuButton v-if="isComputerDevice"
            :config="{
              buttonText: uploadAddItemText,
              defaultEnabled: true,
              height: 30,
              color: 'gray',
            }" @filePicker="openFilePicker" @folderPicker="openFolderPicker" />
            <Button v-else
            :config="{
              buttonText: uploadAddItemText,
              defaultEnabled: true,
              height: 30,
              color: 'gray',
            }" @click="openFilePicker" />
            <Button :config="uploadBtnCfg" ref="uploadBtn" @click="uploadItems" />
          </div>
          <div class="foot-button-wrapper-success" v-show="isUploadedSuccess">
            <MenuButton :config="{
              buttonText: $T('file_request', 'add_more_files'),
              defaultEnabled: true,
              height: 30,
              color: 'gray',
            }" @click="onHandleAddFiles" @filePicker="openFilePicker" @folderPicker="openFolderPicker" />
          </div>
          <div class="foot-button-wrapper-failed" v-show="isUploadedFailed">
            <MenuButton :config="{
              buttonText: $T('file_request', 'add_more_files'),
              defaultEnabled: true,
              height: 30,
              color: 'gray',
            }" @click="onHandleAddFiles" @filePicker="openFilePicker" @folderPicker="openFolderPicker" />
            <Button :config="retryButtonConfig" @click="uploadFailedItems" />
          </div>
        </div>
      </div>
    </div>
    <Preview v-show="isPreviewing" ref="previewInst" class="preview-overlay" @close_preview="closePreview"/>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onBeforeUnmount, inject } from 'vue'
  import { useRouter } from 'vue-router'
  import { useStore } from 'vuex'
  import drivewebapi from '@/store/drivewebapi'
  import dragDropUtils from '@/utils/drag-drop'
  import uploadUtils from '@/utils/upload'
  import Header from '@/components/Header.vue'
  import Button from '@/components/Button.vue'
  import MenuButton from '@/components/MenuButton.vue'
  import ProgressBar from '@/components/ProgressBar.vue'
  import UploadTask from '@/components/UploadTask.vue'
  import TextField from '@/components/TextField.vue'
  import Preview from '@/components/Preview.vue'
  import Toast from '@/components/Toast.vue'
  import { v4 as uuidv4 } from 'uuid'
  import { extension, getIconClass } from '@/utils/get-icon'
  import { UploadStatus, UploadIdentifier, UploadItemStatus, CONSTANT } from '@/utils/constant'
  import { $T, formatString, getErrMsg } from '@/utils/texts'
  import { serverDriveFileRequestExpire, serverDriveFileRequestIdentifier, serverDriveFileRequestCreator, serverDriveFileRequestTitle, serverDriveFileRequestDescription, serverDriveFileRequestHasDueDateHour } from '@/utils/serverInject'

  const router = useRouter()
  const store = useStore()

  const requestCreator = ref(serverDriveFileRequestCreator())
  const escapeRequestTitle = ref(serverDriveFileRequestTitle())
  const escapeRequestDescription = ref(serverDriveFileRequestDescription())
  const requestIdentifier = ref(serverDriveFileRequestIdentifier())
  const requestExpire = ref(serverDriveFileRequestExpire() * 1000) // ms

  const fileInputRef = ref(null)
  const fileInput = ref(null)
  const folderInput = ref(null)
  const uploadErrMsg = ref('')
  const uploadFiles = ref([])
  const rootId = ref('')
  const prevRootId = ref('')
  const uploadTargetId = ref('')
  const currentUploadType = ref('')
  const userName = ref('')
  const prevUserName = ref('')
  const isComposing = ref(false)
  const uploadBtn = ref(null)
  const isValidUserName = ref(false)
  const userNameErrorMessage = ref('')
  const isDragging = ref(false)
  const uploadIdx = ref(0)
  const previewInst = ref(null)
  const isPreviewing = ref(false)
  const windowMgr = inject('windowMgr')
  const scrollPosition = ref(0)
  const toastInst = ref(null)

  let lastTimeUploadSuccessItem = []

  const idleFiles = computed(() => {
    return uploadFiles.value.filter((fileObj) => {
      return fileObj.status === UploadItemStatus.IDLE || fileObj.status === UploadItemStatus.SIZE_EXCEED
    })
  })

  const uploadingFiles = computed(() => {
    return uploadFiles.value.filter((fileObj) => {
      return fileObj.status === UploadItemStatus.UPLOADING
    })
  })

  const uploadRestFiles = computed(() => {
    return uploadFiles.value.filter((fileObj) => {
      return fileObj.status === UploadItemStatus.WAITING
    })
  })

  const uploadSuccessFiles = computed(() => {
    return uploadFiles.value.filter((fileObj) => {
      return fileObj.status === UploadItemStatus.SUCCESS
    })
  })

  const uploadFailedFiles = computed(() => {
    return uploadFiles.value.filter((fileObj) => {
      return fileObj.status === UploadItemStatus.FAILED || fileObj.status === UploadItemStatus.PARTIAL_FAILED
    })
  })

  const uploadTaskItemFiles = computed(() => {
    return [
      ...idleFiles.value,
      ...uploadingFiles.value,
      ...uploadRestFiles.value,
      ...uploadFailedFiles.value,
      ...uploadSuccessFiles.value,
    ]
  })

  const isComputerDevice = computed(() => {
    const innerWidth = window.innerWidth
    const userAgent = navigator.userAgent.toLowerCase()
    const isTouchDevice = "ontouchstart" in window
    const isMobile = /iphone|ipad|ipod|android.*mobile|windows phone/.test(userAgent) || isTouchDevice && innerWidth <= 768
    const isPad = /ipad|android(?!.*mobile)/.test(userAgent) || isTouchDevice && innerWidth > 768 && innerWidth <= 1366

    return !isMobile && !isPad
  })

  const uploadAddItemText = computed(() => {
    if (!isComputerDevice.value) {
      return $T('file_request', 'add_files_mobile')
    }

    return $T('file_request', 'add_files')
  })

  const uploadHintText = computed(() => {
    if (!isComputerDevice.value) {
      // mobile or pad not support drag and drop, so hide the hint
      return ''
    }

    return $T('file_request', 'drag_file_hint')
  })

  const decodeHTML = (html) => {
    const txt = document.createElement('textarea')
    txt.innerHTML = html
    return txt.value
  }

  const requestDisplayTitle = computed(() => {
    return formatString($T('file_request', 'file_request_title'), requestCreator.value, decodeHTML(escapeRequestTitle.value))
  })

  const requestDisplayDescription = computed(() => {
    return decodeHTML(escapeRequestDescription.value)
  })

  const uploadDate = computed(() => {
    if (serverDriveFileRequestHasDueDateHour()) {
      return formatString($T('file_request', 'expiration_hint'), new Date(requestExpire.value).toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }))
    }

    return formatString($T('file_request', 'expiration_hint'), new Date(requestExpire.value).toLocaleDateString())
  })

  const retryButtonConfig = computed(() => ({
    buttonText: uploadFailedFiles.value.length === 1 ? formatString($T('file_request', 'retry_item'), 1) : formatString($T('file_request', 'retry_items'), uploadFailedFiles.value.length),
    defaultEnabled: true,
    width: 120,
    height: 30,
    color: 'gray',
  }))

  const uploadBtnCfg = computed(() => ({
    buttonText: $T('file_request', 'upload_btn'),
    defaultEnabled: userName.value.length > 0,
    width: 120,
    height: 30,
  }))

  const uploadSuccessText = computed (() => {
    if (uploadSuccessFiles.value.length === 1) {
      return formatString($T('file_request', 'upload_success_title_single'), uploadSuccessFiles.value.length)
    }

    return formatString($T('file_request', 'upload_success_title'), uploadSuccessFiles.value.length)
  })

  const uploadFailedText = computed (() => {
    if (uploadFailedFiles.value.length === 1) {
      return formatString($T('file_request', 'upload_failed_title_single'), '1')
    }

    return formatString($T('file_request', 'upload_failed_title'), uploadFailedFiles.value.length)
  })

  const isTotalFileExceedUploadLimitBytes = computed(() => {
    const totalSize = uploadFiles.value.reduce((acc, fileObj) => {
      if (fileObj.type === 'folder') {
        return acc + fileObj.fileInfos.reduce((acc, fileInfo) => {
          return acc + fileInfo.size
        }, 0)
      }

      return acc + fileObj.size
    }, 0)

    return totalSize > CONSTANT.UPLOAD_TOTAL_LIMIT_BYTES
  })

  const isSingleFileExceedUploadLimitBytes = computed(() => {
    return uploadFiles.value.some((fileObj) => {
      if (fileObj.type === 'folder') {
        return fileObj.fileInfos.some((fileInfo) => {
          return fileInfo.size > CONSTANT.UPLOAD_SINGLE_LIMIT_BYTES
        })
      }

      return fileObj.size > CONSTANT.UPLOAD_SINGLE_LIMIT_BYTES
    })
  })

  const toggleUploadButtonState = () => {
    let isDisabled = false;
    let errorMsg = '';

    if (isTotalFileExceedUploadLimitBytes.value) {
      errorMsg = $T('file_request', 'total_size_exceed_limit');
      isDisabled = true;
    } else if (isSingleFileExceedUploadLimitBytes.value) {
      errorMsg = $T('file_request', 'single_size_exceed_limit');
      isDisabled = true;
    }

    if (!isValidUserName.value) {
      isDisabled = true;
    }

    uploadErrMsg.value = errorMsg;
    uploadBtn.value.setEnabled(!isDisabled);
  }

  const isUploadedSuccess = computed(() => {
    return uploadStatus.value === UploadStatus.UPLOADED && uploadFailedFiles.value.length === 0
  })

  const isUploadedFailed = computed(() => {
    return uploadStatus.value === UploadStatus.UPLOADED && uploadFailedFiles.value.length > 0
  })

  const isSelectedFiles = computed(() => {
    return uploadFiles.value.length > 0 && uploadStatus.value === UploadStatus.SELECTED
  })

  const isIdle = computed(() => {
    return uploadStatus.value === UploadStatus.IDLE
  })

  const isUploading = computed(() => {
    return uploadStatus.value === UploadStatus.UPLOADING
  })

  const isUploaded = computed(() => {
    return uploadStatus.value === UploadStatus.UPLOADED
  })

  const hasExpiration = computed(() => {
    return requestExpire.value > 0
  })

  // progress bar
  const progressTitle = ref('')
  const progressText = computed(() => {
    if (uploadStatus.value === UploadStatus.UPLOADING) {
      return (uploadSuccessFiles.value.length + uploadFailedFiles.value.length) + ' / ' + uploadFiles.value.length
    }
    return $T('file_request', 'loading')
  })
  const progressPercentage = computed(() => {
    return (uploadSuccessFiles.value.length + uploadFailedFiles.value.length) / uploadFiles.value.length * 100
  })

  const uploadStatus = ref(UploadStatus.IDLE)
  const nameField = ref(null)

  const filesAreaHeight = computed(() => {
    return uploadTaskItemFiles.value.length > 0 ? '438px' : '420px'
  })

  const handleDropFiles = async (items) => {
    const newAddedFiles = []
    const entryInfos = items.map(item => ({
      entry: item.webkitGetAsEntry ? item.webkitGetAsEntry() : item.getAsEntry(),
      file: item.getAsFile()
    }))

    for (const { entry, file } of entryInfos) {
      const fileObj = {
        id: uuidv4(),
        name: entry.name,
        entry,
      }

      if (entry.isDirectory) {
        fileObj.size = '-'
        fileObj.type = 'folder'
        fileObj.fileInfos = []
        fileObj.file = {
          name: entry.name
        }

        const fileSelect = await dragDropUtils.toFileSelected(fileObj)
        if (fileSelect.containEmptyFolder) {
          const durationMilliSeconds = 3000

          toastInst.value.show($T('file_request', 'empty_folder_upload_warning'), durationMilliSeconds)

          if (fileSelect.fileInfos.length === 0) {
            continue
          }
        }

        fileObj.fileInfos = fileSelect.fileInfos
        fileObj.fileInfos.forEach(fileInfo => {
          fileInfo.uploadId = generateUploadId()
          fileInfo.isUploadSuccessInFolder = false
        })

        if (fileObj.fileInfos.some(fileInfo => fileInfo.size > CONSTANT.UPLOAD_SINGLE_LIMIT_BYTES)) {
          fileObj.status = UploadItemStatus.SIZE_EXCEED
        } else {
          fileObj.status = UploadItemStatus.IDLE
        }
      } else {
        fileObj.size = file.size
        fileObj.type = 'file'
        fileObj.file = file
        fileObj.status = file.size > CONSTANT.UPLOAD_SINGLE_LIMIT_BYTES ? UploadItemStatus.SIZE_EXCEED : UploadItemStatus.IDLE
        fileObj.uploadId = generateUploadId()
      }

      newAddedFiles.push(fileObj)
    }

    uploadFiles.value.unshift(...newAddedFiles)
  }
  const handleSelectedFiles = async (items) => {
    const newAddedFiles = []
    for (const file of items) {
      const fileObj = {
        id: uuidv4(),
        name: file.name,
        size: file.size,
        type: 'file',
        file: file,
        status: file.size > CONSTANT.UPLOAD_SINGLE_LIMIT_BYTES ? UploadItemStatus.SIZE_EXCEED : UploadItemStatus.IDLE,
        uploadId: generateUploadId(),
      }

      newAddedFiles.push(fileObj)
    }

    uploadFiles.value.unshift(...newAddedFiles)
  }

  const checkFileReadability = async (file) => {
    return new Promise((resolve, reject) => {
      const chunkSize = 1024 * 1024 * 1
      const reader = new FileReader()
      const chunk = file.slice(0, chunkSize) // Read first 1MB to check readability

      reader.onload = () => resolve()
      reader.onerror = (evt) => reject(evt.target.error)
      reader.readAsArrayBuffer(chunk)
    })
  }

  const handleSelectedFolder = async (items) => {
    const newAddedFiles = []
    const folderName = items[0].webkitRelativePath.split('/')[0]
    const fileObj = {
      id: uuidv4(),
      name: folderName,
      size: '-',
      type: 'folder',
      fileInfos: [],
      file: {
        name: folderName,
      },
      status: UploadItemStatus.IDLE,
    }

    for (const item of items) {
      try {
        await checkFileReadability(item)
      } catch (error) {
        // Skip the file if not found (ex: Windows path exceeds 260 characters)
        if (error.name === 'NotFoundError') {
          // print DOMException error message
          console.error('Error:', error.message, error.code, error.name, 'File path:', item.webkitRelativePath)
          continue
        }
      }

      fileObj.fileInfos.push({
        content: item, // File object
        relativePath: item.webkitRelativePath,
        size: item.size,
        id: uuidv4(),
        uploadId: generateUploadId(),
        isUploadSuccessInFolder: false,
      })
    }

    if (fileObj.fileInfos.length === 0) {
      const durationMilliSeconds = 3000

      toastInst.value.show($T('file_request', 'empty_folder_upload_warning'), durationMilliSeconds)
      return
    }

    if (fileObj.fileInfos.some(fileInfo => fileInfo.size > CONSTANT.UPLOAD_SINGLE_LIMIT_BYTES)) {
      fileObj.status = UploadItemStatus.SIZE_EXCEED
    }

    newAddedFiles.push(fileObj)
    uploadFiles.value.unshift(...newAddedFiles)
  }

  const onAfterHandleSelectedFiles = () => {
    if (uploadFiles.value.length === 0) {
      return
    }

    toggleUploadButtonState()

    uploadStatus.value = UploadStatus.SELECTED
  }

  const resetUploadStatus = () => {
    uploadFiles.value = []
    uploadStatus.value = UploadStatus.IDLE
    progressPercentage.value = 0
  }

  const onHandleAddFiles = () => {
    if (uploadStatus.value === UploadStatus.UPLOADED) {
      resetUploadStatus()
    }
  }

  const openFilePicker = () => {
    fileInput.value.value = ''
    fileInputRef.value.click()
  }

  const openFolderPicker = () => {
    folderInput.value.value = ''
    folderInput.value.click()
  }

  const onBeforeHandleSelectedFiles = () => {
    if (isUploaded.value) {
      resetUploadStatus()
    }
    uploadErrMsg.value = ''
  }

  const handleDragEnter = (event, status) => {
    if (Array.from(event.dataTransfer.items).filter(item => item.kind === 'file').length === 0) {
      return
    }

    if (status !== UploadStatus.IDLE && status !== UploadStatus.SELECTED) {
      return
    }

    isDragging.value = true
  }

  const handleDragLeave = (event, status) => {
    if (status !== UploadStatus.IDLE && status !== UploadStatus.SELECTED) {
      return
    }

    if (isUploading.value) {
      return
    }

    if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) {
      isDragging.value = false
    }
  }

  const handleDrop = async (event, status) => {
    const dataTransferItems = Array.from(event.dataTransfer.items).filter(item => item.kind === 'file')

    if (dataTransferItems.length === 0) {
      return
    }

    if (status !== UploadStatus.IDLE && status !== UploadStatus.SELECTED) {
      return
    }

    isDragging.value = false

    event.preventDefault()
    onBeforeHandleSelectedFiles()
    await handleDropFiles(dataTransferItems)
    onAfterHandleSelectedFiles()
  }

  const handleFileChange = (event) => {
    event.preventDefault()
    onBeforeHandleSelectedFiles()
    handleSelectedFiles(event.target.files)
    onAfterHandleSelectedFiles()
  }

  const handleFolderChange = async (event) => {
    event.preventDefault()
    onBeforeHandleSelectedFiles()
    await handleSelectedFolder(event.target.files)
    onAfterHandleSelectedFiles()
  }

  onMounted(() => {
    fileInputRef.value = document.querySelector('.container input[type=file]')
    registerWindowLeaveEvent()
  })

  onBeforeUnmount(() => {
    unregisterWindowLeaveEvent()
  })

  const validateUserName = (userName) => {
    // check if username is empty
    if (userName.length === 0) {
      return ''
    }

    // check if username contains <>:"/\|?*
    const reg = /[<>:"/\\|?*]/g
    if (reg.test(userName)) {
      return $T('file_request', 'username_err_special_char')
    }

    // check if username exceeds 60 characters
    const nameLength = new TextEncoder().encode(userName).length
    if (nameLength > CONSTANT.USER_NAME_MAX_LENGTH) {
      return formatString($T('file_request', 'username_err_max_length'), CONSTANT.USER_NAME_MAX_LENGTH)
    }

    // check if username contains only spaces
    if (userName.trim() === '') {
      return $T('file_request', 'username_err_only_space')
    }

    return ''
  }

  const handleUserNameError = (errMsg) => {
    const hasError = !!errMsg

    nameField.value.setErrorState(hasError)
    userNameErrorMessage.value = errMsg
  }

  const onUserNameInput = (event) => {
    if (isComposing.value) {
      return
    }

    // must update userName.value now, otherwise some browsers (ex: Firefox) will not update the value yet
    userName.value = event.target.value

    const errMsg = validateUserName(userName.value)
    handleUserNameError(errMsg)
    isValidUserName.value = !errMsg && userName.value.length > 0
    toggleUploadButtonState()
  }

  const onCompositionStart = () => {
    isComposing.value = true
  }

  const onCompositionEnd = (event) => {
    isComposing.value = false
    onUserNameInput(event)
  }

  const updateProgressTitle = (title) => {
    progressTitle.value = title
  }

  const updateProgressIconAndValue = (uploadTaskItem) => {
    if (uploadTaskItem.type === 'folder') {
      currentUploadType.value = 'folder'
    } else {
      currentUploadType.value = getIconClass(extension(uploadTaskItem.name))
    }
  }

  const initProgressBar = (uploadTaskItem) => {
    if (uploadStatus.value === UploadStatus.UPLOADING) {
      let ProgressTitle = ''

      if (uploadFiles.value.length === 1) {
        ProgressTitle = formatString($T('file_request', 'uploading_item'), 1)
      } else {
        ProgressTitle = formatString($T('file_request', 'uploading_items'), uploadFiles.value.length)
      }

      updateProgressTitle(ProgressTitle)
    }

    updateProgressIconAndValue(uploadTaskItem)
  }

  const showDialog = async (title, message, confirmText, cancelText, resolveFunc) => {
    const btn = await windowMgr.getMsgBox().confirm(title, message, {
      confirm: {
        text: confirmText
      },
      cancel: {
        text: cancelText
      }
    })

    if (btn === 'confirm') {
      resolveFunc()
    }
  }

  const handleClose = () => {
    store.commit('sharingState', 'file_request_closed')
    const message = $T('file_request', 'err_msg_closed')
    const buttonText = $T('common', 'ok')
    showDialog('', message, buttonText, '', () => {
      router.push('/closed')
    })
  }

  const handleError = (errState, message) => {
    store.commit('sharingState', errState)
    const buttonText = $T('common', 'ok')
    showDialog('', message, buttonText, '', () => {
      router.push('/notfound')
    })
  }

  const handleExpire = () => {
    handleError('file_request_expired', $T('file_request', 'err_msg_expire'))
  }

  const prepareUpload = async () => {
    let requestInfo = null

    try {
      requestInfo = await drivewebapi.getRequestInfo()

      if (requestInfo.file_request_status === 'inactive') {
        handleClose()
        return false
      }

      if (requestInfo.is_expired) {
        handleExpire()
        return false
      }

      if (requestInfo.file_request_status === 'error') {
        handleError('file_request_invalid', $T('file_request', 'destination_folder_deleted'))
        return false
      }
    } catch (error) {
      console.error('Failed to get request info:', error)
      if (error.code === 1055) { // DRIVE_ERR_FILE_REQ_LINK
        handleError('file_request_invalid', $T('file_request', 'destination_folder_deleted'))
        return false
      }

      if (error.code === 1057) { // DRIVE_ERR_FILE_REQ_LINK_EXPIRED
        handleExpire()
        return false
      }

      if (error.code === 1076) { // DRIVE_ERR_FILE_REQ_LINK_DISABLED
        handleClose()
        return false
      }

      store.commit('sharingState', 'file_request_invalid')
      let message = $T('error', 'file_request_error')

      if (error.code === 1031) { // DRIVE_ERR_ACCESS_TOKEN_INVALID
        message = $T('error', 'access_token_invalid')
      }

      const buttonText = $T('common', 'ok')
      showDialog('', message, buttonText, '', () => {
        router.push('/notfound')
      })

      return false
    }

    rootId.value = requestInfo.file_id

    // remove leading and trailing spaces
    userName.value = userName.value.trim()

    if (requestIdentifier.value === UploadIdentifier.CREATE_FOLDER) {
      // only create folder while the upload target folder or username changed
      if (prevRootId.value !== rootId.value || prevUserName.value !== userName.value) {
        try {
          const resp = await drivewebapi.createFolder(rootId.value, userName.value, 'autorename')
          uploadTargetId.value = resp.file_id
        } catch (error) {
          uploadErrMsg.value = $T('file_request', 'err_create_folder')
          return false
        }
      }
    } else if (requestIdentifier.value === UploadIdentifier.ADD_PREFIX) {
      uploadTargetId.value = rootId.value
      uploadFiles.value.forEach((fileObj) => {
        fileObj.truncatedName = getTruncatedFileName(userName.value, fileObj.name, fileObj.type)
        fileObj.prefixName = addPrefix(userName.value, fileObj.truncatedName)
        fileObj.prefix = userName.value
      })
    }

    return true
  }

  const onBeforeUpload = async () => {
    uploadIdx.value = 0

    if (await prepareUpload() === false) {
      return false
    }

    // update status
    uploadStatus.value = UploadStatus.UPLOADING

    uploadFiles.value.forEach((fileObj) => {
      fileObj.status = UploadItemStatus.WAITING
    })

    uploadFiles.value[0].status = UploadItemStatus.UPLOADING

    initProgressBar(uploadTaskItemFiles.value[0])
    return true
  }

  const onUploadItem = (status, errMsg) => {
    const currentFile = uploadFiles.value[uploadIdx.value]
    currentFile.status = status
    currentFile.errMsg = ''

    if (status === UploadItemStatus.FAILED || status === UploadItemStatus.PARTIAL_FAILED) {
      if (errMsg) {
        currentFile.errMsg = errMsg
      }
    }

    // update progress bar if there are more files to upload
    if (uploadIdx.value < uploadFiles.value.length - 1) {
      const nextFile = uploadFiles.value[uploadIdx.value + 1]

      if (nextFile.status === UploadItemStatus.WAITING) {
        nextFile.status = UploadItemStatus.UPLOADING
        updateProgressIconAndValue(nextFile)
      }
    }

    uploadIdx.value++
  }

  const Upload = async (conflictAction) => {
    lastTimeUploadSuccessItem = []
    for (let i = 0; i < uploadFiles.value.length; i++) {
      const nodeInfo = uploadFiles.value[i]

      if (nodeInfo.status === UploadItemStatus.SUCCESS) {
        continue
      }

      if (!uploadFiles.value.includes(nodeInfo)) {
        i--
        continue
      }

      try {
        if (nodeInfo.type === 'folder') {
          for (const fileInfo of nodeInfo.fileInfos) {
            await checkFileReadability(fileInfo.content)
          }
          await uploadUtils.uploadFolder(uploadTargetId.value, nodeInfo, conflictAction)
        } else {
          await checkFileReadability(nodeInfo.file)
          await uploadUtils.uploadFile(uploadTargetId.value, nodeInfo, conflictAction)
        }
        onUploadItem(UploadItemStatus.SUCCESS)
        lastTimeUploadSuccessItem.push(nodeInfo)
      } catch (error) {
        console.error('Failed to upload item:', nodeInfo.name, ' Error:', error, ', message: ', error.message, ', code: ', error.code, ', name: ', error.name)
        if (nodeInfo.type === 'folder') {
          if (Array.isArray(error)) {
            error.message = error
                .map((err, index) => {
                  // to handle external error ex: network error
                  if (err instanceof Error) {
                    return `Item[${index + 1}][${err.relativePath}]: ${err.message}`
                  }

                  let errorMessage = err.message || err.toString() // Check if err.errors.message exists, otherwise fallback
                  if (err.errors) {
                    errorMessage = err.errors.message || err.errors.toString() // Check if err.errors.message exists, otherwise fallback
                  }

                  return `Item[${index + 1}][${err.relativePath}]: ${errorMessage}`
                })
          }
          onUploadItem(UploadItemStatus.PARTIAL_FAILED, error.message)
          lastTimeUploadSuccessItem.push(nodeInfo) // will create folder, still need to notify file request creator
        } else {
          onUploadItem(UploadItemStatus.FAILED, getErrMsg(error))
        }
      }
    }
  }

  const onAfterUpload = async () => {
    uploadStatus.value = UploadStatus.UPLOADED
    const ProgressTitle = uploadFiles.value.length === 1 ? formatString($T('file_request', 'uploaded_item'), 1) : formatString($T('file_request', 'uploaded_items'), uploadFiles.value.length)
    updateProgressTitle(ProgressTitle)

    if (lastTimeUploadSuccessItem.length > 0) {
      const fileNameItems = lastTimeUploadSuccessItem.map(fileObj => fileObj.name)
      const fileId = uploadTargetId.value

      try {
        await drivewebapi.notifyUploadComplete({
          uploader: userName.value,
          file_id: fileId,
          request_title: decodeHTML(escapeRequestTitle.value),
          path: `id:${uploadTargetId.value}`,
        }, fileNameItems)
      } catch (error) {
        console.error('Failed to notify upload complete:', error)
      }
    }

    prevRootId.value = rootId.value
    prevUserName.value = userName.value
  }

  const uploadItems = async () => {
    if (await onBeforeUpload() === false) {
      return
    }
    const conflictAction = 'autorename'
    await Upload(conflictAction)

    onAfterUpload()
  }

  const uploadFailedItems = async () => {
    uploadFiles.value = uploadTaskItemFiles.value.slice()
    uploadStatus.value = UploadStatus.UPLOADING

    uploadFiles.value.forEach((fileObj) => {
      if (fileObj.status === UploadItemStatus.FAILED || fileObj.status === UploadItemStatus.PARTIAL_FAILED) {
        fileObj.status = UploadItemStatus.WAITING
        fileObj.id = uuidv4() // drop uv cache
      }
    })
    uploadIdx.value = 0
    uploadFiles.value[0].status = UploadItemStatus.UPLOADING
    const conflictAction = 'version'
    await Upload(conflictAction)
    onAfterUpload()
  }

  const removeItem = (idx) => {
    const file = uploadTaskItemFiles.value[idx]
    const index = uploadFiles.value.findIndex((fileObj) => fileObj.id === file.id)

    uploadFiles.value.splice(index, 1)

    if (uploadTaskItemFiles.value.length === 0) {
      uploadStatus.value = UploadStatus.IDLE
      return
    }

    if (uploadTaskItemFiles.value.length === uploadSuccessFiles.value.length) {
      uploadStatus.value = UploadStatus.UPLOADED
      return
    }

    toggleUploadButtonState()
  }

  const previewItem = (index) => {
    // prevent scrolling
    scrollPosition.value = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollPosition.value}px`
    document.body.style.width = '100%'

    isPreviewing.value = true
    previewInst.value.init(index, uploadTaskItemFiles.value)
  }

  const closePreview = () => {
    // enable scrolling
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    window.scrollTo(0, scrollPosition.value)

    isPreviewing.value = false
  }

  const addPrefix = (prefix, fileName) => {
    return `${prefix}_${fileName}`
  }

  const getTruncatedFileName = (prefix, fileName, fileType) => {
    const MAX_LENGTH = 250  // posix file name length limit is 255, but we need to reserve some space for autorename
    const prefixLength = new TextEncoder().encode(prefix).length
    const fileNameLength = new TextEncoder().encode(fileName).length
    const fullNameLength = prefixLength + fileNameLength + 1 // 1 is for '_'

    if (fullNameLength <= MAX_LENGTH) {
      return fileName
    }

    let extensionLength = 0
    let ext = ''

    if (fileType === 'file') {
      const extIndex = fileName.lastIndexOf('.')
      ext = extIndex !== -1 ? fileName.slice(extIndex) : ''

      extensionLength = new TextEncoder().encode(ext).length
    }

    const availableLength = MAX_LENGTH - prefixLength - extensionLength - 1

    // continuously remove last character of filename and truncate it if larger than available length

    let removeCharLength = 0
    for (let i = fileName.length - 1; i >= 0; i--) {
      removeCharLength += new TextEncoder().encode(fileName[i]).length

      if (fileNameLength - removeCharLength <= availableLength) {
        const truncatedName = fileName.slice(0, i)
        return `${truncatedName}${ext}`
      }
    }
  }

  const handleBeforeUnload = (event) => {
    if (isSelectedFiles.value || isUploading.value) {
      event.preventDefault()
      event.returnValue = true
    }
  }

  const registerWindowLeaveEvent = () => {
    window.addEventListener('beforeunload', handleBeforeUnload)
  }

  const unregisterWindowLeaveEvent = () => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
  }

  const generateUploadId = () => {
    return uuidv4().replaceAll('-', '')
  }

</script>

<style lang="scss" scoped>
#app {
  @extend %font-family-style;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}
.container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.content {
  display: flex;
  padding: 74px 0px 36px 0px;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
}
.body-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.toast {
  top: 12px;
  position: fixed;
  z-index: 10;
}
.title-wrapper {
  display: flex;
  width: 480px;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  .request-title {
    align-self: stretch;
    font-weight: 600;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    color: #2E3032;
    text-align: center;
    line-height: 24px;
    word-wrap: break-word;
    word-break: break-word;
  }
  .request-desc {
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    overflow: hidden;
    color: #7E8082;
    text-align: center;
    text-overflow: ellipsis;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 21px;
    word-wrap: break-word;
    word-break: break-word;
    white-space: pre-wrap;
  }
  .upload-date {
    display: flex;
    padding: 0px 16px;
    align-items: center;
    gap: 10px;
    border-radius: 32px;
    background: #F6F8FA;
    color: #7E8082;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }
}
.drag-drop-area {
  position: absolute;
  display: flex;
  width: 480px;
  height:100%;
  padding: 0px 156px 0px 155px;
  box-sizing: border-box;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 12px;
  border: 1px solid #1EB9C7;
  background: #DDF3F3;
  .drag-drop-icon-wraper {
    width: 169px;
    height: 118px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    .drag-drop-top-hint {
      background-image: image-set(
        url('@/assets/1x/icon_drag_and_drop.gif') 1x,
        url('@/assets/2x/icon_drag_and_drop.gif') 2x
      );
      background-size: 40px 40px;
      width: 72px;
      height: 72px;
      background-color: rgba(255, 255, 255, 0.9);
      border-radius: 50%;
      display: inline-block;
      background-repeat: no-repeat;
      background-position: 16px;
    }
    .drag-drop-bottom-hint {
      margin-top: 14px;
      height: 34px;
      border-radius: 17px;
      min-width: 156px;
      background-color: #19ADB1;
      font-size: 15px;
      font-weight: bold;
      padding: 0 22px;
      color: #fff;
      line-height: 34px;
    }
  }
}
.files-area {
  display: flex;
  width: 480px;
  padding: 0px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 12px;
  box-sizing: border-box;
  .img-btn-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    align-self: stretch;
    -webkit-user-select: none;
    user-select: none;
    .empty-file {
      content: url('@/assets/img_empty_file.svg');
      width: 140px;
      height: 140px;
      flex-shrink: 0;
    }
    .btn-desc-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      align-self: stretch;
    }
  }
}
.foot-button-wrapper {
  display: flex;
  width: 479px;
  justify-content: space-between;
  align-items: flex-start;
}
.foot-button-wrapper-success {
  display: flex;
  width: 479px;
  justify-content: center;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  position: absolute;
}
.foot-button-wrapper-failed {
  display: flex;
  width: 479px;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  position: absolute;
}
.bottom-area {
  display: flex;
  width: 480px;
  height: auto;
  flex-direction: column;
  gap: 36px;

  .username-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    .username-label {
      width: 192px;
      color: #2E3032;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px; /* 171.429% */
    }
    .inupt-username {
      width: 280px;
      height: 30px;
      border-radius: 3px;
      border: 1px solid #D8DADC;
      background: #FFF;
      padding: 5px 10px;
    }
  }
}
.upload-status {
  display: flex;
  width: 480px;
  height: 64px;
  padding: 12px 24px;
  align-items: center;
  gap: 12px;
  border-radius: 12px;
  border: 1px solid #D8DADC;
  box-sizing: border-box;
}
.uploaded-complete-info {
  display: flex;
  width: 480px;
  justify-content: left;
  align-items: center;
  gap: 12px;
  .uploaded-complete-icon {
    display: flex;
    width: 24px;
    height: 24px;
    padding: 4px;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
  }
  .uploaded-complete-text {
    overflow: hidden;
    color: #2E3032;
    text-overflow: ellipsis;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
  }
}

.upload-hint {
  color:  #7E8082;
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
}

.username-msg-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  .error-message-container {
    display: flex;
    position: absolute;
    margin-top: 60px;
    .error-space {
      width: 200px;
    }
    .error-message {
      display: flex;
      min-height: 20px;
      flex-direction: column;
      justify-content: center;
      color: #F2616D;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
    }
  }
}

@media (max-width: 624px) {
  .content {
    padding: 24px;
    width: calc(100% - 48px);
  }
  .upload-status {
    width: 100%;
  }
  .title-wrapper {
    width: 100%;
  }
  .request-title {
    display: flex;
    align-self: stretch;
    color: #2E3032;
    padding-top: 50px;
    text-align: center;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    word-wrap: break-word;
    word-break: break-word;
    justify-content: center;
  }

  .uv {
    width: 100%;
    height: 100%;
    position: absolute;
  }

  .request-desc {
    display: flex;
    align-self: stretch;
    color: #7E8082;
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 21px;
    word-wrap: break-word;
    word-break: break-word;
    justify-content: center;
  }

  .files-area {
    width: 100%;
    min-height: 285px;
    text-align: center;
    gap: 0px;
    border: 1px dashed #B8B8B8;
    background: #FCFCFC;
    display: flex;
    margin-bottom: 20px;
    padding: 0;
    .upload-task {
      width: 100%;
    }
  }
  .bottom-area {
    width: 100%;
    height: 130px;
    display: flex;
    flex-direction: column;
    justify-content: right;

    .username-wrapper {
      display: flex;
      margin-bottom: 20px;
      width: 100%;
      .username-label {
        width: 100%;
        display: inline-block;
        text-align: left;
        margin-right: 10px;
      }
      .inupt-username {
        flex-grow: 1;
        margin-left: auto;
        height: 36px;
        padding-left: 10px;
      }
      .input-wrapper {
        width: 100%;
      }
    }

    .foot-button-wrapper, {
      width: 100%;
    }

    .foot-button-wrapper-success, .foot-button-wrapper-failed {
      width: calc(100% - 48px);
    }
  }
}
</style>
